import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createExampleHistory,
  deleteExampleHistory,
  ExampleHistoryApiError,
  listExampleHistory,
  parseHistoryNumericId,
  patchExampleHistory,
  type ExampleHistoryApiEntry,
} from '../services/exampleHistoryService';

export type SharedCalculatorPropertyType = 'private' | 'ec' | 'hdb';
export type SharedIncomeBasis = 'assessable' | 'gross';

export interface SharedCalculatorProfile {
  id: string;
  age: number;
  monthlyIncome: number;
  monthlyDebts: number;
  cashSavingsMonthly?: number;
  cpfOAMonthly?: number;
  currentCPFOA?: number;
  currentCashSavings?: number;
  selfEmployedInd?: number;
  incomeBasis?: SharedIncomeBasis;
}

export interface SharedCalculatorHistoryEntry {
  id: string;
  label: string;
  timestamp: Date;
  profiles: SharedCalculatorProfile[];
  propertyType?: SharedCalculatorPropertyType;
  purchasePlannerState?: {
    affordabilityState?: {
      inputs?: unknown;
    };
    targetPurchasePrice?: string;
    manualEdits?: Record<
      number,
      {
        cashSavings?: number;
        cpfRefundFromSales?: number;
        cashProceedsFromSale?: number;
      }
    >;
    buyingCalculatorState?: unknown;
    sellingCalculatorState?: unknown;
    decouplingCalculatorState?: unknown;
    /** Profile/group loaded when the entry was saved — lets Profile Manager reload it. */
    savedProfileRef?: { type: 'profile' | 'group'; id: number };
    /** Calculator hosting the Save to History click — picks the Profile Manager button. */
    savedFromCalculator?: 'buying' | 'selling' | 'maximizer' | 'decoupling';
  };
}

export interface SharedCalculatorActiveSelection {
  id: string;
  source: 'example' | 'history';
  profiles: readonly SharedCalculatorProfile[];
  propertyType?: SharedCalculatorPropertyType;
  purchasePlannerState?: SharedCalculatorHistoryEntry['purchasePlannerState'];
}

export const SHARED_CALCULATOR_EXAMPLES = {
  example1: {
    name: 'Example 1: Single Buyer (Age 30, $8k)',
    profiles: [
      {
        id: '1',
        age: 30,
        monthlyIncome: 8000,
        monthlyDebts: 0,
        incomeBasis: 'assessable' as const,
      },
    ],
    propertyType: 'private' as const,
  },
  example2: {
    name: 'Example 2: Single Buyer (Age 45, $8k)',
    profiles: [
      {
        id: '1',
        age: 45,
        monthlyIncome: 8000,
        monthlyDebts: 0,
        incomeBasis: 'assessable' as const,
      },
    ],
    propertyType: 'private' as const,
  },
  example3: {
    name: 'Example 3: Two Buyers (Age 30 $4k + Age 35 $7k)',
    profiles: [
      {
        id: '1',
        age: 30,
        monthlyIncome: 4000,
        monthlyDebts: 0,
        incomeBasis: 'assessable' as const,
      },
      {
        id: '2',
        age: 35,
        monthlyIncome: 7000,
        monthlyDebts: 0,
        incomeBasis: 'assessable' as const,
      },
    ],
    propertyType: 'private' as const,
  },
} as const;

export const formatSharedHistoryTimestamp = (date: Date): string =>
  date.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Entry names are stored in a `varchar(100)` column and the API rejects anything
 *  longer with a 400. The save path reports failures as `null`, so an over-long
 *  label (a long profile or group name plus the timestamp) used to make every
 *  save fail with nothing shown to the agent. Clamp instead of failing. */
export const MAX_HISTORY_LABEL_LENGTH = 100;

export const clampHistoryLabel = (label: string): string =>
  label.length <= MAX_HISTORY_LABEL_LENGTH ? label : label.slice(0, MAX_HISTORY_LABEL_LENGTH).trimEnd();

/** `<name> - <datetime>`, shortening the name — never the timestamp — to fit the cap. */
const labelWithTimestamp = (name: string, ts: string): string => {
  const suffix = ` - ${ts}`;
  const room = MAX_HISTORY_LABEL_LENGTH - suffix.length;
  return `${name.length > room ? name.slice(0, room).trimEnd() : name}${suffix}`;
};

/** When a named profile (super picker) or preset example is active, default save label is `<name> - <datetime>`. */
export function buildLoadedProfileHistoryLabel(
  profileName: string | null | undefined,
  activeSelection: SharedCalculatorActiveSelection | null,
  at: Date = new Date()
): string | undefined {
  const ts = formatSharedHistoryTimestamp(at);
  const trimmed = profileName?.trim();
  if (trimmed) return labelWithTimestamp(trimmed, ts);

  if (activeSelection?.source === 'example') {
    const key = activeSelection.id as keyof typeof SHARED_CALCULATOR_EXAMPLES;
    if (key in SHARED_CALCULATOR_EXAMPLES) {
      const ex = SHARED_CALCULATOR_EXAMPLES[key];
      const base = ex.name.split(':')[0]?.trim() || ex.name;
      return labelWithTimestamp(base, ts);
    }
  }
  return undefined;
}

const ACTIVE_SELECTION_STORAGE_KEY = 'superCalculatorActiveProfileSelection';
const SYNC_EVENT = 'super-calculator-shared-profile-history-updated';
const MAX_HISTORY_ENTRIES = 50;

const cloneProfiles = (profiles: readonly SharedCalculatorProfile[]): SharedCalculatorProfile[] =>
  profiles.map((profile) => ({ ...profile }));

const readActiveSelection = (): SharedCalculatorActiveSelection | null => {
  try {
    const saved = localStorage.getItem(ACTIVE_SELECTION_STORAGE_KEY);
    if (!saved) return null;

    const parsed = JSON.parse(saved) as SharedCalculatorActiveSelection;
    return {
      ...parsed,
      profiles: cloneProfiles(parsed.profiles ?? []),
    };
  } catch (error) {
    console.error(
      `Error loading shared calculator active selection from localStorage (${ACTIVE_SELECTION_STORAGE_KEY}):`,
      error
    );
    return null;
  }
};

const emitHistorySync = () => {
  window.dispatchEvent(new Event(SYNC_EVENT));
};

const saveActiveSelection = (selection: SharedCalculatorActiveSelection | null) => {
  try {
    if (selection) {
      localStorage.setItem(
        ACTIVE_SELECTION_STORAGE_KEY,
        JSON.stringify({
          ...selection,
          profiles: cloneProfiles(selection.profiles),
        })
      );
    } else {
      localStorage.removeItem(ACTIVE_SELECTION_STORAGE_KEY);
    }
    emitHistorySync();
  } catch (error) {
    console.error(
      `Error saving shared calculator active selection to localStorage (${ACTIVE_SELECTION_STORAGE_KEY}):`,
      error
    );
  }
};

function mapApiEntryToHistoryEntry(row: ExampleHistoryApiEntry): SharedCalculatorHistoryEntry {
  const rawProfiles = row.profiles;
  const profilesList = Array.isArray(rawProfiles) ? rawProfiles : [];
  const created = row.createdAt ? new Date(row.createdAt) : new Date();

  return {
    id: `history-${row.id}`,
    label: row.name,
    timestamp: created,
    profiles: cloneProfiles(profilesList as SharedCalculatorProfile[]),
    propertyType: row.propertyType as SharedCalculatorPropertyType | undefined,
    purchasePlannerState: row.purchasePlannerState as SharedCalculatorHistoryEntry['purchasePlannerState'],
  };
}

export const useSharedProfileHistory = () => {
  const [historyEntries, setHistoryEntries] = useState<SharedCalculatorHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [activeSelection, setActiveSelectionState] = useState<SharedCalculatorActiveSelection | null>(() =>
    readActiveSelection()
  );
  const [editingHistoryId, setEditingHistoryId] = useState<string | null>(null);
  const [editingHistoryLabel, setEditingHistoryLabel] = useState<string>('');
  // Why the most recent save/rename/delete failed. Those calls resolve to
  // `null`/`false` on any error, which on its own is indistinguishable from a
  // successful no-op — the caller reads this so the agent is told what happened.
  const lastHistoryErrorRef = useRef<string | null>(null);
  const recordHistoryError = (action: string, context: Record<string, unknown>, error: unknown) => {
    const status = error instanceof ExampleHistoryApiError ? error.status : undefined;
    const message = error instanceof Error ? error.message : String(error);
    lastHistoryErrorRef.current = status ? `${message} (HTTP ${status})` : message;
    console.error(`Failed to ${action} shared calculator history:`, { status, message, ...context, error });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setHistoryLoading(true);
      try {
        const rows = await listExampleHistory();
        if (!cancelled) {
          setHistoryEntries(rows.map(mapApiEntryToHistoryEntry).slice(0, MAX_HISTORY_ENTRIES));
        }
      } catch (error) {
        console.error('Failed to load shared calculator history:', error);
        if (!cancelled) {
          setHistoryEntries([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncActiveSelection = () => {
      setActiveSelectionState(readActiveSelection());
    };

    window.addEventListener('storage', syncActiveSelection);
    window.addEventListener(SYNC_EVENT, syncActiveSelection);

    return () => {
      window.removeEventListener('storage', syncActiveSelection);
      window.removeEventListener(SYNC_EVENT, syncActiveSelection);
    };
  }, []);

  const formatTimestamp = formatSharedHistoryTimestamp;

  const saveToHistory = async (
    entry: {
      profiles: readonly SharedCalculatorProfile[];
      propertyType?: SharedCalculatorPropertyType;
      purchasePlannerState?: SharedCalculatorHistoryEntry['purchasePlannerState'];
    },
    customLabel?: string
  ): Promise<SharedCalculatorHistoryEntry | null> => {
    const timestamp = new Date();
    const historyCount = historyEntries.length + 1;
    const defaultLabel = clampHistoryLabel(
      customLabel || `History ${historyCount} - ${formatTimestamp(timestamp)}`
    );

    lastHistoryErrorRef.current = null;
    try {
      const created = await createExampleHistory({
        name: defaultLabel,
        profiles: cloneProfiles(entry.profiles),
        propertyType: entry.propertyType,
        purchasePlannerState: entry.purchasePlannerState,
      });
      const mapped = mapApiEntryToHistoryEntry(created);
      setHistoryEntries((prev) =>
        [mapped, ...prev.filter((e) => e.id !== mapped.id)].slice(0, MAX_HISTORY_ENTRIES)
      );
      return mapped;
    } catch (error) {
      recordHistoryError(
        'save',
        { label: defaultLabel, labelLength: defaultLabel.length, profileCount: entry.profiles.length },
        error
      );
      return null;
    }
  };

  /** Why the last save/rename/delete failed. Read straight after awaiting the
   *  call — a ref, so it is never a render behind. */
  const getLastHistoryError = useCallback(() => lastHistoryErrorRef.current, []);

  const loadFromHistory = (historyId: string): SharedCalculatorHistoryEntry | null => {
    const entry = historyEntries.find((item) => item.id === historyId);
    return entry
      ? {
          ...entry,
          profiles: cloneProfiles(entry.profiles),
        }
      : null;
  };

  const setActiveSelection = useCallback((selection: SharedCalculatorActiveSelection | null) => {
    const nextSelection = selection
      ? {
          ...selection,
          profiles: cloneProfiles(selection.profiles),
        }
      : null;
    setActiveSelectionState(nextSelection);
    saveActiveSelection(nextSelection);
  }, []);

  const updateHistoryLabel = async (historyId: string, newLabel: string): Promise<boolean> => {
    if (!newLabel.trim()) {
      lastHistoryErrorRef.current = 'Name cannot be empty';
      return false;
    }

    const numericId = parseHistoryNumericId(historyId);
    if (numericId == null) {
      lastHistoryErrorRef.current = 'This entry cannot be renamed';
      console.warn('Cannot rename history: invalid id', historyId);
      return false;
    }

    const nextLabel = clampHistoryLabel(newLabel.trim());
    lastHistoryErrorRef.current = null;
    try {
      const updated = await patchExampleHistory(numericId, { name: nextLabel });
      const mapped = mapApiEntryToHistoryEntry(updated);
      setHistoryEntries((entries) => entries.map((e) => (e.id === historyId ? mapped : e)));
    } catch (error) {
      recordHistoryError('rename', { historyId, labelLength: nextLabel.length }, error);
      return false;
    }

    setEditingHistoryId(null);
    setEditingHistoryLabel('');
    return true;
  };

  const startEditingLabel = (historyId: string) => {
    const entry = historyEntries.find((item) => item.id === historyId);
    if (!entry) return;

    setEditingHistoryId(historyId);
    setEditingHistoryLabel(entry.label);
  };

  const cancelEditing = () => {
    setEditingHistoryId(null);
    setEditingHistoryLabel('');
  };

  const deleteHistory = async (historyId: string): Promise<boolean> => {
    const numericId = parseHistoryNumericId(historyId);
    if (numericId == null) {
      lastHistoryErrorRef.current = 'This entry cannot be deleted';
      console.warn('Cannot delete history: invalid id', historyId);
      return false;
    }

    lastHistoryErrorRef.current = null;
    try {
      await deleteExampleHistory(numericId);
    } catch (error) {
      recordHistoryError('delete', { historyId }, error);
      return false;
    }

    setHistoryEntries((entries) => entries.filter((entry) => entry.id !== historyId));

    const currentActiveSelection = readActiveSelection();
    if (currentActiveSelection?.source === 'history' && currentActiveSelection.id === historyId) {
      setActiveSelection(null);
    }
    return true;
  };

  return {
    historyEntries,
    historyLoading,
    activeSelection,
    editingHistoryId,
    editingHistoryLabel,
    setEditingHistoryLabel,
    saveToHistory,
    getLastHistoryError,
    loadFromHistory,
    setActiveSelection,
    updateHistoryLabel,
    startEditingLabel,
    cancelEditing,
    deleteHistory,
    formatTimestamp,
  };
};
